import {
  attachmentsFromMessage,
  contentLooksLikeAttachment,
} from './boardChatAttachmentUtils';

export function contactNameFromConversation(conv) {
  if (!conv) return 'Contato';
  const meta = conv.meta || {};
  const sender = meta.sender || meta.assignee || {};
  return (
    conv.contact?.name ||
    sender.name ||
    meta.sender?.name ||
    conv.additional_attributes?.name ||
    `#${conv.id}`
  );
}

/** Status canônico da conversa (Chatwoot). Nunca inferir de filtro de UI. */
export function conversationStatusOf(conv) {
  if (!conv || typeof conv !== 'object') return '';
  return String(conv.status ?? conv.status_name ?? '').trim().toLowerCase();
}

/** ID do contato Chatwoot (meta.sender.id) — usado p/ nota persistente. */
export function contactIdFromConversation(conv) {
  const id = conv?.meta?.sender?.id ?? conv?.contact?.id ?? null;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Nota em custom_attributes.nota do contato (sobrevive entre conversas). */
export function contactNoteFromConversation(conv) {
  const sender = conv?.meta?.sender || conv?.contact || {};
  const attrs = sender.custom_attributes || sender.customAttributes || {};
  return String(attrs.nota ?? attrs.note ?? '').trim();
}

function lastMessageSource(conv) {
  return (
    conv?.last_non_activity_message ||
    conv?.messages?.[0] ||
    null
  );
}

/** Preview amigável — nunca expõe raw `__attachment__:...`. */
export function lastMessagePreview(conv) {
  const msg = lastMessageSource(conv);
  const content = String(msg?.content || conv?.last_message || '').trim();
  const attachList = msg
    ? attachmentsFromMessage(msg)
    : contentLooksLikeAttachment(content)
      ? [{}]
      : [];
  if (attachList.length || contentLooksLikeAttachment(content)) {
    if (attachList.length === 1 && attachList[0]?.caption) {
      const cap = String(attachList[0].caption);
      return cap.length > 80 ? `${cap.slice(0, 80)}…` : cap;
    }
    return 'Arquivos';
  }
  if (!content) return 'Sem mensagens';
  return content.length > 80 ? `${content.slice(0, 80)}…` : content;
}

export function lastMessageIsFiles(conv) {
  const msg = lastMessageSource(conv);
  const content = String(msg?.content || conv?.last_message || '').trim();
  if (contentLooksLikeAttachment(content)) return true;
  if (msg && attachmentsFromMessage(msg).length) return true;
  return false;
}

export function relativeTime(ts) {
  if (ts == null) return '';
  const ms = typeof ts === 'number' ? (ts < 1e12 ? ts * 1000 : ts) : Date.parse(ts);
  if (!Number.isFinite(ms)) return '';
  const diff = Math.max(0, Date.now() - ms);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export function messageTimeLabel(ts) {
  if (ts == null) return '';
  const ms = typeof ts === 'number' ? (ts < 1e12 ? ts * 1000 : ts) : Date.parse(ts);
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const hour = String(d.getHours()).padStart(2, '0');
  const minute = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month} ${hour}:${minute}`;
}

/** Chatwoot: 0 incoming, 1 outgoing, 2 activity; ou strings. */
export function normalizeMessageType(msg) {
  if (msg?.private) return 'private';
  const t = msg?.message_type;
  if (t === 0 || t === 'incoming') return 'incoming';
  if (t === 1 || t === 'outgoing') return 'outgoing';
  if (t === 2 || t === 'activity') return 'activity';
  return 'incoming';
}

/**
 * Segmenta texto e emojis para renderizar o emoji maior.
 * @returns {{ type: 'text'|'emoji', value: string }[]}
 */
export function splitTextAndEmojis(content) {
  const text = String(content ?? '');
  if (!text) return [];
  try {
    const re = /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)/gu;
    const parts = [];
    let last = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) {
        parts.push({ type: 'text', value: text.slice(last, m.index) });
      }
      parts.push({ type: 'emoji', value: m[0] });
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
    return parts.length ? parts : [{ type: 'text', value: text }];
  } catch {
    return [{ type: 'text', value: text }];
  }
}

/** Mensagem composta só por emoji(s). */
export function isEmojiOnlyContent(content) {
  const t = String(content || '').trim();
  if (!t) return false;
  try {
    if (!/\p{Extended_Pictographic}/u.test(t)) return false;
    const stripped = t
      .replace(/\p{Extended_Pictographic}(\uFE0F|\u200D\p{Extended_Pictographic})*/gu, '')
      .replace(/[\uFE0F\u200D\s]/gu, '');
    return stripped.length === 0;
  } catch {
    return false;
  }
}

/** Traduz textos de atividade do Chatwoot (EN) para pt-BR. */
export function translateActivityMessage(content) {
  const raw = String(content || '').trim();
  if (!raw) return 'Atividade';

  const rules = [
    [
      /^System reopened the conversation due to a new incoming message\.?$/i,
      'O sistema reabriu a conversa devido a uma nova mensagem recebida.',
    ],
    [
      /^Conversation was marked resolved by (.+) due to inactivity\.?$/i,
      (_, user) => `Conversa marcada como resolvida por ${user} devido à inatividade.`,
    ],
    [
      /^Conversation was marked resolved by system due to (\d+) days of inactivity\.?$/i,
      (_, n) => `Conversa marcada como resolvida pelo sistema após ${n} dias de inatividade.`,
    ],
    [
      /^Conversation was marked resolved by system due to (\d+) hours of inactivity\.?$/i,
      (_, n) => `Conversa marcada como resolvida pelo sistema após ${n} horas de inatividade.`,
    ],
    [
      /^Conversation was marked resolved by system due to (\d+) minutes of inactivity\.?$/i,
      (_, n) => `Conversa marcada como resolvida pelo sistema após ${n} minutos de inatividade.`,
    ],
    [
      /^Conversation was marked resolved by (.+)\.?$/i,
      (_, user) => `Conversa marcada como resolvida por ${user}.`,
    ],
    [
      /^Conversation was resolved by (.+)\.?$/i,
      (_, name) => `Conversa resolvida por ${name}.`,
    ],
    [
      /^Conversation was marked as pending by (.+)\.?$/i,
      (_, user) => `Conversa marcada como pendente por ${user}.`,
    ],
    [
      /^Conversation was marked open by system due to an error with the agent bot\.?$/i,
      'Conversa marcada como aberta pelo sistema devido a um erro no bot do agente.',
    ],
    [
      /^Conversation was marked open by (.+)\.?$/i,
      (_, user) => `Conversa marcada como aberta por ${user}.`,
    ],
    [
      /^Conversation was reopened by (.+)\.?$/i,
      (_, user) => `Conversa reaberta por ${user}.`,
    ],
    [
      /^Conversation was snoozed by (.+)\.?$/i,
      (_, user) => `Conversa adiada por ${user}.`,
    ],
    [
      /^Assigned to (.+) via (.+) by (.+)\.?$/i,
      (_, assignee, team, user) =>
        `Atribuída a ${assignee} via ${team} por ${user}.`,
    ],
    [
      /^Assigned to (.+) by (.+)\.?$/i,
      (_, target, user) => `Atribuída a ${target} por ${user}.`,
    ],
    [
      /^(.+) self-assigned this conversation\.?$/i,
      (_, user) => `${user} atribuiu esta conversa a si.`,
    ],
    [
      /^Conversation unassigned by (.+)\.?$/i,
      (_, user) => `Conversa desatribuída por ${user}.`,
    ],
    [
      /^Unassigned from (.+) by (.+)\.?$/i,
      (_, team, user) => `Removida do setor ${team} por ${user}.`,
    ],
    [
      /^(.+) has muted the conversation\.?$/i,
      (_, user) => `${user} silenciou a conversa.`,
    ],
    [
      /^(.+) has unmuted the conversation\.?$/i,
      (_, user) => `${user} removeu o silêncio da conversa.`,
    ],
    [
      /^(.+) set the priority to (.+)\.?$/i,
      (_, user, priority) => `${user} definiu a prioridade como ${priority}.`,
    ],
    [
      /^(.+) changed the priority from (.+) to (.+)\.?$/i,
      (_, user, from, to) => `${user} alterou a prioridade de ${from} para ${to}.`,
    ],
    [
      /^(.+) removed the priority\.?$/i,
      (_, user) => `${user} removeu a prioridade.`,
    ],
    [
      /^(.+) added (.+)$/i,
      (_, user, labels) => `${user} adicionou ${labels}.`,
    ],
    [
      /^(.+) removed (.+)$/i,
      (_, user, labels) => `${user} removeu ${labels}.`,
    ],
    [
      /^This message was deleted\.?$/i,
      'Esta mensagem foi apagada.',
    ],
    [
      /^Resolving the conversation as it has been inactive for a while\. Please start a new conversation if you need further assistance\.?$/i,
      'Resolvendo a conversa por inatividade. Inicie uma nova conversa se precisar de mais ajuda.',
    ],
  ];

  for (const [pattern, replacement] of rules) {
    const match = raw.match(pattern);
    if (!match) continue;
    return typeof replacement === 'function'
      ? replacement(...match)
      : replacement;
  }

  return raw;
}

export function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function avatarHue(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

export function assigneeFromConversation(conv) {
  const a = conv?.meta?.assignee || conv?.assignee || null;
  if (!a) return null;
  const name = a.name || a.available_name || '';
  return {
    id: a.id ?? a.user_id ?? null,
    name: name || '—',
    email: a.email || '',
    initial: firstInitialFromName(name || a.email || ''),
  };
}

/**
 * Conversa atribuída a outro agente Chatwoot → somente visualização.
 * Sem assignee (fila) ou assignee = usuário atual → pode interagir.
 */
export function isConversationAssignedToOther(conv, chatwootUserId) {
  const assignee = assigneeFromConversation(conv);
  if (!assignee?.id) return false;
  const me = Number(chatwootUserId);
  if (!Number.isFinite(me) || me <= 0) return true;
  return Number(assignee.id) !== me;
}

/**
 * Chatwoot usa first_reply_created_at=0 (ou "0") quando ainda não houve
 * resposta de agente — não contar como progresso.
 */
export function conversationHasAgentFirstReply(conv) {
  const v = conv?.first_reply_created_at;
  if (v == null || v === '') return false;
  if (typeof v === 'number') return v > 0;
  const s = String(v).trim();
  if (!s || s === '0') return false;
  const n = Number(s);
  if (Number.isFinite(n)) return n > 0;
  // ISO / timestamp textual não-numérico → houve resposta
  return true;
}

/**
 * Conversa sem assignee → precisa Assumir atendimento.
 *
 * Chat normal (não pré): sempre que estiver na fila (sem assignee), inclusive
 * após “Devolver para fila” — ignora first_reply para não liberar o composer
 * para todos.
 *
 * Pré-atendimento com OS: Assumir se todas pending/sem dono (não usa first_reply —
 * nota de abertura de OS preenche isso cedo demais).
 *
 * Pré-atendimento sem OS: Assumir só se ainda não houve 1ª resposta do agente
 * (evita reexibir após OS excluídas / fluxo já vivido).
 */
export function isConversationAwaitingAssume(
  conv,
  chatwootUserId,
  osList = [],
  { isPreAtendimento = false } = {}
) {
  if (!conv) return false;
  const status = conversationStatusOf(conv);
  if (status === 'resolved') return false;
  if (assigneeFromConversation(conv)?.id) return false;
  const me = Number(chatwootUserId);
  if (!Number.isFinite(me) || me <= 0) return false;

  const arr = Array.isArray(osList) ? osList : [];

  if (arr.length === 0) {
    if (!isPreAtendimento) return true;
    if (conversationHasAgentFirstReply(conv)) return false;
    return true;
  }

  for (const item of arr) {
    if (osAssignedAgentId(item) != null) return false;
    const st = String(item?.status || 'pending').toLowerCase();
    if (st === 'forwarded' || st === 'closed' || st === 'snoozed') return false;
  }

  return true;
}

/** assignedAgentId da OS (handoff pré-atendimento). */
export function osAssignedAgentId(item) {
  if (!item || typeof item !== 'object') return null;
  const raw = item.assignedAgentId ?? item.assigned_agent_id;
  if (raw == null || String(raw).trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Já houve algum repasse nesta conversa (pelo menos uma OS com dono). */
export function conversationOsHandoffActive(osList) {
  return (Array.isArray(osList) ? osList : []).some((item) => osAssignedAgentId(item) != null);
}

/**
 * Assignee Chatwoot só trava a UI antes do 1º handoff de OS.
 * Depois disso vale a trava do dono da OS pendente mais antiga (envio público).
 */
export function isConversationInteractionLocked(conv, chatwootUserId, osList) {
  if (!isConversationAssignedToOther(conv, chatwootUserId)) return false;
  if (conversationOsHandoffActive(osList)) return false;
  return true;
}

/**
 * Dono da vez: assigned_agent_id da OS pendente mais antiga com dono
 * (lista já vem ORDER BY id ASC do bridge/MySQL).
 */
export function getPendingOsHolderAgentId(osList) {
  for (const item of Array.isArray(osList) ? osList : []) {
    if (String(item?.status || 'pending').toLowerCase() !== 'pending') continue;
    const aid = osAssignedAgentId(item);
    if (aid != null) return aid;
  }
  return null;
}

/**
 * OS pendentes que travam o envio ao cliente: as do holder no início da fila
 * (consecutivas até aparecer outro dono). Uma ou mais.
 */
export function getBlockingPendingOsIds(osList) {
  const holderId = getPendingOsHolderAgentId(osList);
  if (holderId == null) return [];
  const out = [];
  for (const item of Array.isArray(osList) ? osList : []) {
    if (String(item?.status || 'pending').toLowerCase() !== 'pending') continue;
    const aid = osAssignedAgentId(item);
    if (aid == null) continue;
    if (aid !== holderId) break;
    const id = String(item?.osId || item?.os_id || '').trim();
    if (id) out.push(id);
  }
  return out;
}

/**
 * Retomar (snoozed→pending): o agente escolhe qual OS atender.
 * Não bloqueia por outra OS pendente dele nem de outro (sem auto-adiar).
 */
export function isOsRetomarBlocked(_osList, _snoozedOsId, _agentId) {
  return false;
}

export const OS_RETOMAR_BLOCKED_TIP =
  'Não é possível retomar esta OS no momento.';

/** OS pendentes/adiadas com dono = agentId. */
export function getOsOwnedByAgent(osList, agentId) {
  const me = Number(agentId);
  if (!Number.isFinite(me) || me <= 0) return [];
  return (Array.isArray(osList) ? osList : []).filter((item) => {
    const st = String(item?.status || 'pending').toLowerCase();
    if (st !== 'pending' && st !== 'snoozed') return false;
    return osAssignedAgentId(item) === me;
  });
}

/** OS pendentes/adiadas com dono diferente de agentId. */
export function getPendingOsOwnedByOtherAgents(osList, agentId) {
  const me = Number(agentId);
  if (!Number.isFinite(me) || me <= 0) return [];
  return (Array.isArray(osList) ? osList : []).filter((item) => {
    const st = String(item?.status || 'pending').toLowerCase();
    if (st !== 'pending' && st !== 'snoozed') return false;
    const aid = osAssignedAgentId(item);
    return aid != null && aid !== me;
  });
}

/**
 * Atendente sem OS própria, mas conversa segue com OS de outro (pós encaminhar/fechar).
 * Candidato a “Sair do atendimento” sem Resolver.
 */
export function canLeaveAttendanceForPeerOs(osList, agentId) {
  if (getOsOwnedByAgent(osList, agentId).length > 0) return false;
  return getPendingOsOwnedByOtherAgents(osList, agentId).length > 0;
}

/**
 * Somente leitura: agente sem OS pendente/adiada, conversa com ≥ 2 OS,
 * já houve encaminhamento/fechamento, e ainda há OS ativa de outro (ou na fila).
 * Não aplica em abertura nova (todas pending sem dono) — evita falso positivo.
 * Volta a editar se receber OS via repasse.
 */
export function isConversationReadOnlyForAgent(osList, agentId) {
  const arr = Array.isArray(osList) ? osList : [];
  if (arr.length < 2) return false;
  const me = Number(agentId);
  if (!Number.isFinite(me) || me <= 0) return false;
  if (getOsOwnedByAgent(arr, me).length > 0) return false;
  const hasTerminalOs = arr.some((item) => {
    const st = String(item?.status || 'pending').toLowerCase();
    return st === 'forwarded' || st === 'closed';
  });
  if (!hasTerminalOs) return false;
  return arr.some((item) => {
    const st = String(item?.status || 'pending').toLowerCase();
    if (st !== 'pending' && st !== 'snoozed') return false;
    const aid = osAssignedAgentId(item);
    // Sem dono = reivindicável — não trava como “só leitura”
    if (aid == null) return false;
    return aid !== me;
  });
}

/** Há OS pendente/adiada com dono diferente de agentId. */
export function conversationHasOsOwnedByOtherAgent(osList, agentId) {
  return getPendingOsOwnedByOtherAgents(osList, agentId).length > 0;
}

/** Texto compacto: "OS 12" | "OS 12 e 34" | "OS 12, 34 e 56". */
export function formatOsIdListLabel(osIds) {
  const ids = (Array.isArray(osIds) ? osIds : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean);
  if (!ids.length) return '';
  if (ids.length === 1) return `OS ${ids[0]}`;
  if (ids.length === 2) return `OS ${ids[0]} e ${ids[1]}`;
  return `OS ${ids.slice(0, -1).join(', ')} e ${ids[ids.length - 1]}`;
}

/**
 * Linhas do tooltip: cada OS pendente com dono atual (após handoff).
 * agents: [{ id, name, ... }]
 */
export function buildOsAssignmentTipRows(osList, agents = []) {
  const byId = new Map();
  for (const a of Array.isArray(agents) ? agents : []) {
    const id = Number(a?.id ?? a?.user_id);
    if (!Number.isFinite(id) || id <= 0) continue;
    const name = String(a?.name || a?.available_name || a?.email || '').trim();
    if (name) byId.set(id, name);
  }
  const rows = [];
  for (const item of Array.isArray(osList) ? osList : []) {
    const osId = String(item?.osId || item?.os_id || '').trim();
    if (!osId) continue;
    const st = String(item?.status || 'pending').toLowerCase();
    if (st === 'forwarded' || st === 'closed') continue;
    const aid = osAssignedAgentId(item);
    if (aid == null) continue;
    const agentName = byId.get(aid);
    rows.push({
      label: `OS ${osId}`,
      value: agentName ? `@${agentName}` : `agente #${aid}`,
    });
  }
  return rows;
}

/**
 * Escopo de OS pro agente logado após handoff:
 * - sem nenhum assigned → lista completa (comportamento atual)
 * - com handoff ativo → OS atribuídas a este agente + OS sem dono (reivindicáveis)
 */
export function filterOsListForAgent(osList, chatwootUserId) {
  const arr = Array.isArray(osList) ? osList : [];
  if (!conversationOsHandoffActive(arr)) return arr;
  const me = Number(chatwootUserId);
  if (!Number.isFinite(me) || me <= 0) return [];
  return arr.filter((item) => {
    const aid = osAssignedAgentId(item);
    return aid == null || aid === me;
  });
}

/** OS ativa sem assigned_agent_id, com disputa (já existe dono em outra OS). */
export function isContestedUnassignedOs(item, osList) {
  if (!item) return false;
  const st = String(item?.status || 'pending').toLowerCase();
  if (st !== 'pending' && st !== 'snoozed') return false;
  if (osAssignedAgentId(item) != null) return false;
  return conversationOsHandoffActive(osList);
}

/**
 * Menu do chip/card de OS: só Consultar quando outro agente é o dono
 * (ex.: 2 OS na mesma conversa após repasse).
 */
export function isOsOwnedByOtherAgent(item, chatwootUserId) {
  const aid = osAssignedAgentId(item);
  if (aid == null) return false;
  const me = Number(chatwootUserId);
  if (!Number.isFinite(me) || me <= 0) return true;
  return aid !== me;
}

/** consultOnly do menu: conversa ainda não assumida OU OS de outro agente. */
export function isOsChipMenuConsultOnly(item, chatwootUserId, awaitingAssume = false) {
  if (awaitingAssume) return true;
  return isOsOwnedByOtherAgent(item, chatwootUserId);
}

export function listContestedUnassignedOs(osList) {
  const arr = Array.isArray(osList) ? osList : [];
  if (!conversationOsHandoffActive(arr)) return [];
  return arr.filter((item) => isContestedUnassignedOs(item, arr));
}

/** Primeira letra do nome (badge de atendente). */
export function firstInitialFromName(name) {
  const s = String(name || '')
    .trim()
    .replace(/^[^\p{L}\p{N}]+/u, '');
  if (!s) return '?';
  return s.charAt(0).toUpperCase();
}

export function teamIdFromConversation(conv) {
  const raw = conv?.meta?.team?.id ?? conv?.team_id ?? conv?.meta?.team_id ?? null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function teamNameFromConversation(conv, teams = []) {
  const teamId = teamIdFromConversation(conv);
  if (teamId != null) {
    const found = teams.find((t) => Number(t.id) === Number(teamId));
    if (found) return found.name;
  }
  return conv?.meta?.team?.name || null;
}

export function formatBadge(n) {
  const v = Number(n) || 0;
  if (v <= 0) return null;
  if (v > 99) return '99+';
  return String(v);
}

const SESSION_STATUS_LABELS = {
  open: 'Aberta',
  pending: 'Pendente',
  resolved: 'Resolvida',
  snoozed: 'Adiada',
};

export function sessionStatusLabel(status) {
  return SESSION_STATUS_LABELS[String(status || '').toLowerCase()] || status || '—';
}

export function sessionTimeLabel(ts) {
  return messageTimeLabel(ts);
}

/** Formata telefone BR para exibição (somente dígitos → máscara). */
export function formatPhoneDisplay(value) {
  const n = String(value || '').replace(/\D/g, '');
  if (!n.length) return '';
  if (n.length <= 2) return `(${n}`;
  if (n.length <= 6) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
  if (n.length <= 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
  return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7, 11)}`;
}

export function phoneDigitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

/** Máscara limitada (ex.: 10 fixo, 11 WhatsApp). */
export function formatPhoneMaskLimited(value, maxDigits = 11) {
  return formatPhoneDisplay(phoneDigitsOnly(value).slice(0, maxDigits));
}

/** Índice da 1ª mensagem no intervalo da sessão (ou -1). */
export function findMessageIndexForSession(messages, session) {
  if (!session?.startedAt || !Array.isArray(messages) || !messages.length) return -1;
  const startMs = Date.parse(session.startedAt);
  if (!Number.isFinite(startMs)) return -1;
  const endMs = session.endedAt != null ? Date.parse(session.endedAt) : null;

  for (let i = 0; i < messages.length; i += 1) {
    const raw = messages[i]?.created_at;
    const ms =
      typeof raw === 'number' ? (raw < 1e12 ? raw * 1000 : raw) : Date.parse(raw);
    if (!Number.isFinite(ms)) continue;
    if (ms < startMs - 2000) continue;
    if (endMs != null && Number.isFinite(endMs) && ms > endMs + 2000) continue;
    return i;
  }
  return -1;
}

/**
 * Gatilho de refetch a partir do payload ActionCable do Chatwoot.
 * Formato real: { event: 'message.created', data: {...} } (não payload.message.*).
 */
export function shouldRefetchFromCable(raw) {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const event = String(data?.event || '');
    if (!event) return false;
    if (isTypingCableEvent(event)) return false;
    // conversation.updated: patch local de unread_count (sem refetch completo)
    if (event === 'conversation.updated' || event === 'conversation_updated') return false;
    return (
      event === 'message.created' ||
      event === 'message_created' ||
      event === 'conversation.created' ||
      event === 'conversation_created' ||
      event === 'conversation.status_changed' ||
      event === 'assignee.changed' ||
      event === 'assignee_changed' ||
      event === 'team.changed' ||
      event === 'team_changed' ||
      event === 'first.reply.created'
    );
  } catch {
    return false;
  }
}

/**
 * Extrai payload de message.created para badge global (sender + team).
 * @returns {{ senderType: string, teamId: number|null, conversationId: number|null, isIncoming: boolean } | null}
 */
export function parseMessageCreatedCable(raw) {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const event = String(data?.event || '');
    if (event !== 'message.created' && event !== 'message_created') return null;
    const payload = data?.data || data?.message || {};
    const senderType = String(payload?.sender?.type || '').toLowerCase();
    const messageType = payload?.message_type;
    // Chatwoot: 0 = incoming, 1 = outgoing, 2 = activity
    const isIncoming =
      senderType === 'contact' ||
      messageType === 0 ||
      messageType === '0' ||
      String(messageType).toLowerCase() === 'incoming';
    const teamIdRaw =
      payload?.team?.id ??
      payload?.team_id ??
      payload?.conversation?.team_id ??
      payload?.conversation?.team?.id ??
      payload?.conversation?.meta?.team?.id ??
      payload?.meta?.team?.id ??
      payload?.meta?.team_id ??
      null;
    const teamId = teamIdRaw != null ? Number(teamIdRaw) : null;
    const conversationIdRaw =
      payload?.conversation_id ??
      payload?.conversation?.id ??
      payload?.id ??
      null;
    const conversationId = conversationIdRaw != null ? Number(conversationIdRaw) : null;
    return {
      senderType,
      senderName: String(payload?.sender?.name || '').trim(),
      content: typeof payload?.content === 'string' ? payload.content : '',
      hasAttachment: Array.isArray(payload?.attachments) && payload.attachments.length > 0,
      teamId: Number.isFinite(teamId) && teamId > 0 ? teamId : null,
      conversationId:
        Number.isFinite(conversationId) && conversationId > 0 ? conversationId : null,
      isIncoming,
    };
  } catch {
    return null;
  }
}

function isTypingCableEvent(event) {
  return (
    event === 'conversation.typing_on' ||
    event === 'conversation.typing_off' ||
    event === 'typing_on' ||
    event === 'typing_off'
  );
}

/** Patch leve de conversation.updated (ex.: unread_count / status). */
export function parseConversationUpdatedCable(raw) {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const event = String(data?.event || '');
    const isUpdated =
      event === 'conversation.updated' || event === 'conversation_updated';
    const isStatusChanged =
      event === 'conversation.status_changed' || event === 'status_changed';
    if (!isUpdated && !isStatusChanged) return null;
    const payload = data?.data || data?.conversation || {};
    const id = payload?.id ?? payload?.conversation?.id ?? data?.id;
    if (id == null) return null;
    const patch = { id: Number(id) };
    if (payload.unread_count != null) patch.unread_count = Number(payload.unread_count) || 0;
    if ('first_reply_created_at' in payload) {
      patch.first_reply_created_at = payload.first_reply_created_at;
    }
    const statusRaw = payload.status ?? payload.conversation?.status ?? data?.status;
    if (statusRaw != null && String(statusRaw).trim() !== '') {
      patch.status = String(statusRaw).trim().toLowerCase();
    }
    if (payload.last_activity_at != null) patch.last_activity_at = payload.last_activity_at;
    if (payload.timestamp != null) patch.timestamp = payload.timestamp;
    if (payload.meta != null && typeof payload.meta === 'object') {
      patch.meta = payload.meta;
    }
    if (payload.meta?.assignee !== undefined || payload.assignee !== undefined) {
      patch.meta = {
        ...(payload.meta && typeof payload.meta === 'object' ? payload.meta : {}),
        assignee: payload.meta?.assignee !== undefined ? payload.meta.assignee : payload.assignee,
      };
    }
    const teamIdRaw =
      payload?.team?.id ??
      payload?.team_id ??
      payload?.meta?.team?.id ??
      payload?.meta?.team_id ??
      null;
    if (teamIdRaw != null) {
      const teamId = Number(teamIdRaw);
      if (Number.isFinite(teamId) && teamId > 0) patch.teamId = teamId;
    }
    return patch;
  } catch {
    return null;
  }
}

/** Extrai info de digitação do ActionCable (cliente → atendente). */
export function parseTypingCableEvent(raw) {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const event = String(data?.event || '');
    if (!isTypingCableEvent(event)) return null;
    const payload = data?.data || {};
    const conversationId =
      payload?.conversation?.id ??
      payload?.conversation_id ??
      payload?.id ??
      null;
    const isOn =
      event === 'conversation.typing_on' || event === 'typing_on';
    const name =
      payload?.user?.name ||
      payload?.user?.available_name ||
      payload?.sender?.name ||
      'Cliente';
    return {
      isOn,
      conversationId: conversationId != null ? Number(conversationId) : null,
      name: String(name || 'Cliente').trim() || 'Cliente',
    };
  } catch {
    return null;
  }
}
